/**
 * PaywallAdaptyLinks.tsx — Adapty 产品关联面板（重设计 ③，移植自 design paywall-products.jsx AdaptyLinks）
 *
 * server-side 只读镜像（useAdaptyCatalog）只返 Adapty 内部 product_id / title / product_set —— **不返 access_level_id**；
 * 由运营在此把 Adapty 产品（product_id）**手动关联**到某个逻辑产品的 access_level_id（admin 独有表 adapty_product_links，
 * 消费 use-adapty-product-links）。关联状态由「关联表 + 逻辑产品通道齐备度」精确判（未关联 / 已关联·配齐 / 缺通道），
 * 替掉旧的按标题启发式弱校验。默认折叠成一行摘要；未关联产品走 LinkAdaptyDialog 建立关联。
 *
 * 关联固定（A8 §8.3）：active 关联不提供改值；该 access_level 已有渠道行时「解除关联」禁用并写明原因，暂无渠道行时
 * 可解除，解除后该 Adapty 产品只能恢复原值（关联值按关联历史固定，含软删行）。服务端是判定权威，异值提交被拒时
 * toast 显示服务端原因。
 *
 * offer 列（Adapty 介绍性 offer）暂缺：admin catalog DTO 未 surface offer（server-side API 有、需 admin-first 扩展），
 * 记 follow-up（docs/plans），本面板先不显 offer。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { LogicalProduct } from '~/data/adapters/product-metadata-grouping';
import {
  CHANNEL_DISPLAY_ORDER,
  channelRowGapLabel,
  channelStoreLabel,
} from '~/data/adapters/product-metadata-grouping';
import { useAdaptyCatalog } from '~/data/queries';
import { adaptyProductLinksApi } from '~/hooks/use-adapty-product-links';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import type { PanelApp } from './PaywallProductsPanel';

interface MirrorProduct {
  product_id: string;
  title: string;
  product_set: string | null;
}

type LinkStatus =
  | { linked: false; level: 'warn'; label: '未关联' }
  | {
      linked: true;
      level: 'warn';
      label: string;
      al: string;
      missing?: string[];
      /** 存在但已停用的通道文案（缺陷：disabled 非空但 missing 为空时须仍判 warn）*/
      disabled?: string[];
      invalid?: boolean;
    }
  | { linked: true; level: 'ok'; label: '已关联'; al: string };

/** 关联状态：关联表 + 逻辑产品通道齐备度精确判（替掉 name 弱校验）*/
function linkStatus(
  productId: string,
  linkMap: Map<string, string>,
  groups: LogicalProduct[],
  platform: string | undefined,
): LinkStatus {
  const al = linkMap.get(productId);
  if (!al) return { linked: false, level: 'warn', label: '未关联' };
  const g = groups.find((x) => x.accessLevelId === al);
  // 关联先于渠道行建立（A8 §3 创建顺序），该 access_level 暂无渠道行是新商品的正常中间态，不判「失效」
  if (!g) return { linked: true, level: 'warn', label: '暂无渠道行', al, invalid: true };
  const rd = g.readiness;
  if (rd.gaps.length) {
    const miss = rd.gaps.map((gap) => channelRowGapLabel(gap, platform));
    return { linked: true, level: 'warn', label: miss.join(' / '), al, missing: miss };
  }
  // 无缺口但在架行凑不齐（停用导致未齐）→ 仍是 warn（未配齐履约），不能落回「已关联」ok；
  // 可选通道停用而在架行仍齐备时不报
  if (rd.activeGaps.length) {
    const dis = rd.disabled.map((m) => channelStoreLabel(m, platform));
    return { linked: true, level: 'warn', label: `${dis.join(' / ')} 已停用`, al, disabled: dis };
  }
  return { linked: true, level: 'ok', label: '已关联', al };
}

function useCanLink() {
  return useAuthStore(
    (s) => s.user?.role === 'root' || s.user?.role === 'admin' || s.user?.role === 'operator',
  );
}

export function PaywallAdaptyLinks({
  app,
  groups,
  channelRowsReady,
}: {
  app: PanelApp;
  groups: LogicalProduct[];
  /** 渠道行列表已成功加载；加载中 / 失败时 groups 为空不代表「暂无渠道行」，解除入口一律禁用 */
  channelRowsReady: boolean;
}) {
  const noApp = app.id === 'all';
  const catalog = useAdaptyCatalog(app.id);
  const links = useQuery({
    queryKey: ['adapty-product-links', app.id],
    queryFn: () => adaptyProductLinksApi.list(app.id),
    enabled: !noApp,
  });
  const canLink = useCanLink();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [linkDialog, setLinkDialog] = useState<{ product: MirrorProduct } | null>(null);
  // 解除关联（含陈旧孤儿 link）：仅该 access_level 暂无渠道行时可解除；已有渠道行时关联固定（A8 §8.3）
  const releaseMutation = useMutation({
    mutationFn: (id: string) => adaptyProductLinksApi.remove(id),
    onSuccess: async () => {
      toast('已解除关联', { tone: 'pos', icon: 'IconLink' });
      await queryClient.invalidateQueries({ queryKey: ['adapty-product-links', app.id] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '解除失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 镜像产品（跨 placement 去重 by product_id）
  const seen = new Set<string>();
  const mirrorProducts: MirrorProduct[] = [];
  for (const pl of catalog.data?.placements ?? []) {
    for (const pr of pl.products ?? []) {
      if (seen.has(pr.product_id)) continue;
      seen.add(pr.product_id);
      mirrorProducts.push({
        product_id: pr.product_id,
        title: pr.title,
        product_set: pr.product_set,
      });
    }
  }
  const linkMap = new Map((links.data ?? []).map((l) => [l.adapty_product_id, l.access_level_id]));
  const linkIdByProduct = new Map((links.data ?? []).map((l) => [l.adapty_product_id, l.id]));
  // 已有渠道行的 access_level（groups 按未删除的 product_metadata 行归组，含停用行）—— 关联固定、不可解除
  const levelsWithChannelRows = new Set(groups.map((g) => g.accessLevelId));
  const rows = mirrorProducts.map((p) => ({
    p,
    s: linkStatus(p.product_id, linkMap, groups, app.platform),
  }));
  // 陈旧孤儿 link：关联的 Adapty product 已不在当前 catalog 镜像（product 从 Adapty 移除但 link 未解除）。表只渲染
  // mirrorProducts，故不 surface 会让其 access_level 被 takenAccessLevels 标占用、又无处删除 → 新 (app,access_level)
  // 唯一索引下该 access_level 无法重指派。单列出来 + 给「解除关联」入口：该 access_level 暂无渠道行时可释放；
  // 已有渠道行时关联固定（A8 §8.3），解除禁用并写原因。
  const staleLinks = (links.data ?? []).filter((l) => !seen.has(l.adapty_product_id));
  const unlinked = rows.filter((r) => !r.s.linked).length;
  const missingCh = rows.filter((r) => r.s.linked && r.s.level === 'warn').length;
  const rank = (r: (typeof rows)[number]) => (!r.s.linked ? 0 : r.s.level === 'warn' ? 1 : 2);
  const sorted = [...rows].sort((a, b) => rank(a) - rank(b));

  // 无镜像产品且无陈旧 link → 不显本面板（有陈旧 link 时仍显，供运营解除）
  if (mirrorProducts.length === 0 && staleLinks.length === 0) return null;

  const copy = (t: string, l: string) => {
    navigator.clipboard?.writeText(t);
    toast(`已复制 ${l}`, { tone: 'pos', icon: 'IconCopy' });
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button
        type="button"
        className="hstack"
        style={{
          gap: 10,
          width: '100%',
          padding: '10px 14px',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
        }}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icons.IconChevRight
          size={13}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            color: 'var(--fg-muted)',
            flexShrink: 0,
          }}
        />
        <Icons.IconLayers size={14} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500 }}>
            Adapty 产品关联 · server-side 只读镜像
          </span>
          <span style={{ fontSize: 11, color: 'var(--fg-faint)', marginLeft: 8 }}>
            接口返 product_id / title / product_set · access_level 由本后台关联
          </span>
        </div>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {mirrorProducts.length}
        </span>
        {unlinked > 0 ? (
          <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
            <Icons.IconAlert size={9} />
            {unlinked} 未关联
          </span>
        ) : (
          <span className="chip chip-pos" style={{ fontSize: 9.5 }}>
            <Icons.IconCheck size={9} />
            全部已关联
          </span>
        )}
        {missingCh > 0 && (
          <span className="chip chip-info" style={{ fontSize: 9.5 }}>
            {missingCh} 缺通道
          </span>
        )}
        {staleLinks.length > 0 && (
          <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
            <Icons.IconAlert size={9} />
            {staleLinks.length} 镜像已移除
          </span>
        )}
      </button>
      {open && (
        <div style={{ overflowX: 'auto', borderTop: '1px solid var(--border)' }}>
          <Table className="tbl" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>标题</th>
                <th>Adapty product_id</th>
                <th>product_set</th>
                <th>已关联 access_level</th>
                <th>关联状态</th>
                <th style={{ width: 120 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(({ p, s }) => (
                <tr key={p.product_id}>
                  <td style={{ fontSize: 12, fontWeight: 500 }}>{p.title}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ maxWidth: 160 }}
                      title={`复制 ${p.product_id}`}
                      onClick={() => copy(p.product_id, 'product_id')}
                    >
                      <span
                        className="mono"
                        style={{ fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {p.product_id}
                      </span>
                    </button>
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {p.product_set ?? '—'}
                    </span>
                  </td>
                  <td>
                    {s.linked ? (
                      <span className="mono" style={{ fontSize: 11, color: 'var(--accent-fg)' }}>
                        {s.al}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>未关联</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`chip ${s.level === 'ok' ? 'chip-pos' : 'chip-warn'}`}
                      style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                    >
                      {s.level === 'ok' ? (
                        <Icons.IconCheck size={9} />
                      ) : (
                        <Icons.IconAlert size={9} />
                      )}
                      {s.label}
                    </span>
                  </td>
                  <td>
                    {!s.linked ? (
                      <button
                        type="button"
                        className="btn btn-accent btn-sm"
                        disabled={!canLink || noApp}
                        title="把该 Adapty 产品关联到某个支付商品的 access_level"
                        onClick={() => setLinkDialog({ product: p })}
                      >
                        <Icons.IconLink size={10} /> 关联
                      </button>
                    ) : (
                      <ReleaseLinkAction
                        linkId={linkIdByProduct.get(p.product_id)}
                        pinned={levelsWithChannelRows.has(s.al)}
                        channelRowsReady={channelRowsReady}
                        disabled={!canLink || noApp || releaseMutation.isPending}
                        onRelease={(id) => releaseMutation.mutate(id)}
                      />
                    )}
                  </td>
                </tr>
              ))}
              {/* 陈旧孤儿 link（镜像已移除）：单列出来 + 解除入口；其 access_level 暂无渠道行时可释放，已有渠道行时关联固定 */}
              {staleLinks.map((l) => (
                <tr key={`stale-${l.id}`} style={{ opacity: 0.85 }}>
                  <td style={{ fontSize: 12, fontWeight: 500 }}>
                    {l.adapty_title ?? '(镜像已移除)'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ maxWidth: 160 }}
                      title={`复制 ${l.adapty_product_id}`}
                      onClick={() => copy(l.adapty_product_id, 'product_id')}
                    >
                      <span
                        className="mono"
                        style={{ fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {l.adapty_product_id}
                      </span>
                    </button>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>—</span>
                  </td>
                  <td>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--accent-fg)' }}>
                      {l.access_level_id}
                    </span>
                  </td>
                  <td>
                    <span
                      className="chip chip-warn"
                      style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                      title="该 Adapty 产品已不在当前 catalog 镜像；其 access_level 暂无渠道行时可解除以释放"
                    >
                      <Icons.IconAlert size={9} />
                      镜像已移除
                    </span>
                  </td>
                  <td>
                    <ReleaseLinkAction
                      linkId={l.id}
                      pinned={levelsWithChannelRows.has(l.access_level_id)}
                      channelRowsReady={channelRowsReady}
                      disabled={!canLink || noApp || releaseMutation.isPending}
                      title="解除该陈旧关联，释放其 access_level（仅暂无渠道行时可解除）"
                      onRelease={(id) => releaseMutation.mutate(id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {linkDialog && (
        <LinkAdaptyDialog
          app={app}
          product={linkDialog.product}
          groups={groups}
          // 1:1：已被【其它】 Adapty 产品关联的 access_level 集（弹窗只对未关联产品打开）
          takenAccessLevels={
            new Set(
              [...linkMap.entries()]
                .filter(([pid]) => pid !== linkDialog.product.product_id)
                .map(([, al]) => al),
            )
          }
          onClose={() => setLinkDialog(null)}
        />
      )}
    </div>
  );
}

/** 已有渠道行时的禁用原因（A8 §8.3：不得借软删换值，需要另一个 access level 时创建新产品） */
const PINNED_LINK_REASON = '已有渠道行，关联固定不可解除；需要另一个 access level 时请创建新产品';

/** 渠道行列表未成功加载时的禁用原因：空列表不等于暂无渠道行 */
const CHANNEL_ROWS_UNKNOWN_REASON = '渠道行列表未加载，暂不可解除';

/**
 * 「解除关联」：渠道行列表未加载时禁用；已有渠道行时禁用并写明原因（关联固定，A8 §8.3）。服务端仍是判定权威。
 */
function ReleaseLinkAction({
  linkId,
  pinned,
  channelRowsReady,
  disabled,
  title = '解除关联（该 access_level 暂无渠道行）',
  onRelease,
}: {
  linkId: string | undefined;
  pinned: boolean;
  /** 渠道行列表已成功加载；false 时 pinned 无从判定 */
  channelRowsReady: boolean;
  disabled: boolean;
  title?: string;
  onRelease: (id: string) => void;
}) {
  const reason = !channelRowsReady
    ? CHANNEL_ROWS_UNKNOWN_REASON
    : pinned
      ? PINNED_LINK_REASON
      : title;
  return (
    <div className="vstack" style={{ gap: 2, alignItems: 'flex-start' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={disabled || !channelRowsReady || pinned || !linkId}
        title={reason}
        onClick={() => linkId && onRelease(linkId)}
      >
        解除关联
      </button>
      {pinned && (
        <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>已有渠道行 · 关联固定</span>
      )}
    </div>
  );
}

/** 只读镜像 strip（Adapty 承接）—— 成组展示 server-side 接口实返、只读的镜像字段 */
function AdaptyStrip({ children }: { children: ReactNode }) {
  return (
    <div className="card-flat" style={{ padding: 12, background: 'var(--bg-subtle)' }}>
      <div className="hstack" style={{ gap: 8, marginBottom: 8 }}>
        <Icons.IconLock size={11} style={{ color: 'var(--fg-faint)' }} />
        <span style={{ fontSize: 11.5, fontWeight: 500 }}>Adapty Dashboard 承接 · 只读</span>
      </div>
      <div className="hstack" style={{ gap: 22, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

/** 只读字段（label 上 / 值下）*/
function ROField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="vstack" style={{ gap: 2 }}>
      <span className="label" style={{ fontSize: 9.5 }}>
        {label}
      </span>
      <span style={{ fontSize: 12 }}>{children}</span>
    </div>
  );
}

function LinkAdaptyDialog({
  app,
  product,
  groups,
  takenAccessLevels,
  onClose,
}: {
  app: PanelApp;
  product: MirrorProduct;
  groups: LogicalProduct[];
  takenAccessLevels: Set<string>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // 1:1：可选逻辑产品 = 排除已被【其它】 Adapty 产品占用的 access_level
  const selectableGroups = groups.filter((g) => !takenAccessLevels.has(g.accessLevelId));
  const hiddenTakenCount = groups.length - selectableGroups.length;
  const [accessLevel, setAccessLevel] = useState('');
  // 名称辅助校验：Adapty title 与所选逻辑产品名不一致时提示（非阻断）
  const selectedGroup = selectableGroups.find((g) => g.accessLevelId === accessLevel);
  // 新商品先建关联、再录渠道行（A8 §3 创建顺序；§2 渠道行须命中 active 关联）：尚无渠道行的 access_level
  // 不在已录入分组里，只能手填。与下拉互斥：选了已录入商品即清空手填，反之亦然
  const [newAccessLevel, setNewAccessLevel] = useState('');
  const typedAccessLevel = newAccessLevel.trim();
  const typedAccessLevelTaken = typedAccessLevel !== '' && takenAccessLevels.has(typedAccessLevel);
  // 与关联契约同口径（A8 §8.3：1..255、无空白），被其它 Adapty 产品占用的值不收（1:1）
  const typedAccessLevelValid = /^\S{1,255}$/.test(typedAccessLevel) && !typedAccessLevelTaken;
  const targetAccessLevel = selectedGroup
    ? selectedGroup.accessLevelId
    : typedAccessLevelValid
      ? typedAccessLevel
      : '';
  // 确认按钮门：命中【可选】逻辑产品（1:1 + 避免重存坏 access_level），或手填一个合法且未被占用的新值
  const isValidSelection = targetAccessLevel !== '';
  const titleMismatch =
    selectedGroup && selectedGroup.name.trim().toLowerCase() !== product.title.trim().toLowerCase();

  const mutation = useMutation({
    mutationFn: () =>
      adaptyProductLinksApi.upsert({
        app_id: app.id,
        adapty_product_id: product.product_id,
        access_level_id: targetAccessLevel,
        adapty_title: product.title,
      }),
    onSuccess: async () => {
      toast('已关联', { tone: 'pos', icon: 'IconLink' });
      await queryClient.invalidateQueries({ queryKey: ['adapty-product-links', app.id] });
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '关联失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  return (
    <Dialog
      title="关联 Adapty 产品"
      subtitle={`把「${product.title}」关联到一个支付商品的 access_level`}
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            <span className="kbd">Esc</span> 取消
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={!isValidSelection || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '关联中…' : '确认关联'}
          </button>
        </>
      }
    >
      {/* Dialog 壳 body 不带内边距，由弹窗自带；与「新建 App」等弹窗同一套 18px 左右边距 */}
      <div className="vstack" style={{ gap: 12, padding: '14px 18px 18px' }}>
        {/* 只读镜像 strip（Adapty 承接）—— 标题 / product_id / product_set 成组只读展示 */}
        <AdaptyStrip>
          <ROField label="标题">
            <span style={{ fontSize: 12, fontWeight: 500 }}>{product.title}</span>
          </ROField>
          <ROField label="product_id">
            <span className="mono" style={{ fontSize: 11 }}>
              {product.product_id}
            </span>
          </ROField>
          <ROField label="product_set">
            <span className="chip" style={{ fontSize: 10 }}>
              {product.product_set ?? '—'}
            </span>
          </ROField>
        </AdaptyStrip>
        <FormRow
          label="支付商品"
          required
          hint="access_level_id"
          labelWidth={140}
          gap={14}
          pad="4px 0 14px"
          labelPadTop={6}
        >
          <Select
            value={accessLevel}
            placeholder="选择已录入的支付商品"
            onChange={(e) => {
              setAccessLevel(e.target.value);
              setNewAccessLevel('');
            }}
          >
            {selectableGroups.map((g) => (
              <option key={g.accessLevelId} value={g.accessLevelId}>
                {g.name} · {g.accessLevelId}
              </option>
            ))}
          </Select>
          {selectableGroups.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>
              {groups.length === 0
                ? '暂无已录入支付商品 · 新商品请在下方填写 access_level_id 先建关联，再新建支付商品'
                : '全部支付商品的 access_level 均已被其它 Adapty 产品关联（1:1，已有渠道行的关联固定不可解除）· 新商品请在下方填写 access_level_id'}
            </div>
          ) : (
            hiddenTakenCount > 0 && (
              <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
                已隐藏 {hiddenTakenCount} 个被其它 Adapty 产品关联的 access_level（1:1，一个
                access_level 只能关联一个 Adapty 产品）
              </div>
            )
          )}
          <input
            className="input mono"
            aria-label="新商品 access_level_id"
            value={newAccessLevel}
            onChange={(e) => {
              setNewAccessLevel(e.target.value);
              setAccessLevel('');
            }}
            placeholder="或填写新商品的 access_level_id（尚未录入渠道行）"
            style={{ maxWidth: 360, marginTop: 8 }}
          />
          {typedAccessLevelTaken && (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>
              该 access_level 已被其它 Adapty 产品关联（1:1，一个 access_level 只能关联一个 Adapty
              产品）
            </div>
          )}
        </FormRow>
        {/* 选中逻辑产品后的校验预览：标题一致性二态 + 通道齐备度（一条 IAP 行 + 至少一条非 IAP 行）*/}
        {selectedGroup && (
          <div className="card-flat" style={{ padding: 12 }}>
            <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              {titleMismatch ? (
                <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
                  <Icons.IconAlert size={9} />
                  标题不一致（仅辅助校验，不阻断）
                </span>
              ) : (
                <span className="chip chip-pos" style={{ fontSize: 9.5 }}>
                  <Icons.IconCheck size={9} />
                  标题一致
                </span>
              )}
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                Adapty「{product.title}」 / 支付商品「{selectedGroup.name}」
              </span>
            </div>
            <div className="hstack" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {CHANNEL_DISPLAY_ORDER.filter((ch) =>
                selectedGroup.channels.some((c) => c.paymentMethod === ch),
              ).map((ch) => {
                // 停用（有行但 inactive）不画绿勾，额外标注
                const isDisabled = selectedGroup.readiness.disabled.includes(ch);
                return (
                  <span
                    key={ch}
                    className={`chip ${isDisabled ? 'chip-warn' : ''}`}
                    style={{ fontSize: 9.5 }}
                  >
                    {isDisabled ? <Icons.IconAlert size={9} /> : <Icons.IconCheck size={9} />}
                    {channelStoreLabel(ch, app.platform)}
                    {isDisabled && ' · 已停用'}
                  </span>
                );
              })}
              {selectedGroup.readiness.gaps.map((gap) => (
                <span key={gap} className="chip chip-warn" style={{ fontSize: 9.5 }}>
                  <Icons.IconAlert size={9} />
                  {channelRowGapLabel(gap, app.platform)}
                </span>
              ))}
              {selectedGroup.readiness.gaps.length > 0 ? (
                <span style={{ fontSize: 10.5, color: 'var(--warn)' }}>
                  关联后请到主列表补齐通道（
                  {selectedGroup.readiness.gaps
                    .map((gap) => channelRowGapLabel(gap, app.platform))
                    .join(' / ')}
                  ）
                </span>
              ) : selectedGroup.readiness.activeGaps.length > 0 ? (
                <span style={{ fontSize: 10.5, color: 'var(--warn)' }}>
                  关联后请到主列表启用{' '}
                  {selectedGroup.readiness.disabled
                    .map((ch) => channelStoreLabel(ch, app.platform))
                    .join(' / ')}{' '}
                  通道
                </span>
              ) : (
                <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>通道已齐</span>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
